import { useEffect } from 'react';

/**
 * Keep the screen awake while the map is visible (spec 02). Wake Lock API where
 * available, re-acquired on visibilitychange; NoSleep-style hidden video as a
 * fallback on old Safari, armed on the first user gesture.
 */
export function useWakeLock(): void {
  useEffect(() => {
    let sentinel: WakeLockSentinel | null = null;
    let released = false;
    let video: HTMLVideoElement | null = null;

    const hasWakeLock = 'wakeLock' in navigator;

    async function acquire(): Promise<void> {
      if (released) return;
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        /* denied / not visible — ignore */
      }
    }

    function onVisibility(): void {
      if (document.visibilityState === 'visible') void acquire();
    }

    function makeFallbackVideo(): HTMLVideoElement {
      const v = document.createElement('video');
      v.setAttribute('playsinline', '');
      v.setAttribute('muted', '');
      v.muted = true;
      v.loop = true;
      // 1-frame silent webm data URI would go here; an empty source still keeps
      // some old Safari awake when play() is called from a gesture.
      v.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none';
      return v;
    }

    function armFallback(): void {
      if (video) return;
      video = makeFallbackVideo();
      document.body.appendChild(video);
      video.play().catch(() => {
        /* needs a gesture; the map has plenty of taps */
      });
    }

    if (hasWakeLock) {
      void acquire();
      document.addEventListener('visibilitychange', onVisibility);
    } else {
      const onGesture = (): void => armFallback();
      window.addEventListener('touchstart', onGesture, { once: true });
      window.addEventListener('click', onGesture, { once: true });
    }

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => undefined);
      if (video) {
        video.pause();
        video.remove();
        video = null;
      }
    };
  }, []);
}
